import { useEffect, useRef, useState, useCallback } from 'react';

export type ExchangeEventType =
  | 'exchange.started'
  | 'exchange.thinking'
  | 'exchange.step.started'
  | 'exchange.step.completed'
  | 'exchange.step.failed'
  | 'exchange.task.started'
  | 'exchange.task.completed'
  | 'exchange.response.completed'
  | 'exchange.error'
  | 'exchange.input_required'
  | 'exchange.completed'
  | 'exchange.failed'
  | 'conversation.result'
  | 'job.status'
  | 'context.summarized'
  | 'exchange.mode_resolved'
  | 'exchange.subagent.started'
  | 'exchange.subagent.completed'
  | 'exchange.subagent.failed'
  | 'session.stopped'
  | 'artefact.ready'
  | 'plan.created'
  | 'plan.updated'
  | 'datasource.query.started'
  | 'datasource.query.completed'
  | 'datasource.query.failed';

export interface ExchangeEvent {
  type: ExchangeEventType;
  exchange_id?: string;
  sessionId?: string;
  agentName?: string;
  data?: Record<string, any>;
  timestamp?: string;
}

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export interface UseWebSocketOptions {
  url: string;
  token: string | null;
  onEvent: (event: ExchangeEvent) => void;
  enabled?: boolean;
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  connectionState: ConnectionState;
  subscribe: (sessionId: string, agentName?: string) => void;
  unsubscribe: () => void;
  connectionError: string | null;
}

const MAX_RECONNECT_DELAY = 30000;
const INITIAL_RECONNECT_DELAY = 2000;
const CONNECTION_TIMEOUT_MS = 20000;

export function useWebSocket({
  url,
  token,
  onEvent,
  enabled = true,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const onEventRef = useRef(onEvent);
  const subscriptionRef = useRef<{ sessionId: string; agentName?: string } | null>(null);
  const disposedRef = useRef(false);
  const hasConnectedBeforeRef = useRef(false);

  onEventRef.current = onEvent;

  useEffect(() => {
    disposedRef.current = false;

    if (!enabled || !token || !url) {
      setConnectionState('idle');
      return;
    }

    let currentWs: WebSocket | null = null;

    const openConnection = () => {
      if (disposedRef.current) return;
      clearTimeout(connectTimeoutRef.current);

      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      setConnectionState(hasConnectedBeforeRef.current ? 'reconnecting' : 'connecting');

      try {
        const wsUrl = `${url}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(wsUrl);
        currentWs = ws;
        wsRef.current = ws;

        ws.onopen = () => {
          if (disposedRef.current || currentWs !== ws) return;
          clearTimeout(connectTimeoutRef.current);
          hasConnectedBeforeRef.current = true;
          setConnectionState('connected');
          setConnectionError(null);
          reconnectDelayRef.current = INITIAL_RECONNECT_DELAY;

          if (subscriptionRef.current) {
            const msg: Record<string, string> = {
              action: 'subscribe',
              session_id: subscriptionRef.current.sessionId,
            };
            if (subscriptionRef.current.agentName) {
              msg.agent_name = subscriptionRef.current.agentName;
            }
            ws.send(JSON.stringify(msg));
          }
        };

        ws.onmessage = (evt) => {
          if (disposedRef.current) return;
          try {
            const raw = JSON.parse(evt.data);
            const parsed: ExchangeEvent = {
              ...raw,
              type: raw.type || raw.event_type,
              sessionId: raw.sessionId || raw.session_id,
              agentName: raw.agentName || raw.agent_name,
            };
            onEventRef.current(parsed);
          } catch {
            // ignore malformed messages
          }
        };

        ws.onerror = () => {
          if (disposedRef.current) return;
          setConnectionError('WebSocket connection error');
          setConnectionState('error');
        };

        ws.onclose = () => {
          if (disposedRef.current || currentWs !== ws) return;
          clearTimeout(connectTimeoutRef.current);
          wsRef.current = null;

          setConnectionState('reconnecting');
          reconnectTimerRef.current = setTimeout(() => {
            reconnectDelayRef.current = Math.min(
              reconnectDelayRef.current * 2,
              MAX_RECONNECT_DELAY,
            );
            openConnection();
          }, reconnectDelayRef.current);
        };

        connectTimeoutRef.current = setTimeout(() => {
          if (disposedRef.current || wsRef.current !== ws) return;
          if (ws.readyState === WebSocket.OPEN) return;
          setConnectionState('error');
          setConnectionError(
            `WebSocket connection timed out after ${CONNECTION_TIMEOUT_MS / 1000}s. ` +
              'Check that WebSockets to this host are allowed (some corporate networks block wss://).',
          );
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        }, CONNECTION_TIMEOUT_MS);
      } catch (err) {
        if (!disposedRef.current) {
          setConnectionState('error');
          setConnectionError(String(err));
        }
      }
    };

    openConnection();

    return () => {
      disposedRef.current = true;
      clearTimeout(connectTimeoutRef.current);
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      currentWs = null;
      setConnectionState('idle');
    };
  }, [enabled, token, url]);

  const subscribe = useCallback((sessionId: string, agentName?: string) => {
    subscriptionRef.current = { sessionId, agentName };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      const msg: Record<string, string> = {
        action: 'subscribe',
        session_id: sessionId,
      };
      if (agentName) msg.agent_name = agentName;
      ws.send(JSON.stringify(msg));
    }
  }, []);

  const unsubscribe = useCallback(() => {
    subscriptionRef.current = null;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: 'unsubscribe' }));
    }
  }, []);

  const isConnected = connectionState === 'connected';

  return { isConnected, connectionState, subscribe, unsubscribe, connectionError };
}
